/**
 * /foh — HustleEats Front-of-House tablet app.
 *
 * One self-contained page that handles the full Phase 1 service flow:
 *   PIN login → open shift → tap floor plan to seat → add check items
 *   → split/transfer/pay → close shift with cash variance.
 *
 * Web app; install as PWA on iPad/Android (no native wrapper).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Coffee, Lock, LogOut, Plus, Minus, Wallet, DollarSign,
  Move, Split, Combine, X, ArrowRight, Loader2, AlertTriangle,
  CheckCircle2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const SK  = "foh_session";

function hdrs(extra = {}) {
  const t = localStorage.getItem(SK);
  return { "X-FOH-Token": t || "", ...extra };
}

export default function FohTabletPage() {
  const [token, setToken] = useState(localStorage.getItem(SK) || "");
  const [me, setMe]       = useState(null);
  const [tables, setTables] = useState([]);
  const [shift, setShift]   = useState(null);
  const [active, setActive] = useState(null);    // active table for the side drawer
  const [check, setCheck]   = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(SK)) return;
    // Use allSettled so a single hung call (R2 backfill saturation)
    // doesn't blank-out the whole tablet.
    const [m, t, sh] = await Promise.allSettled([
      axios.get(`${API}/api/foh/auth/me`,         { headers: hdrs(), timeout: 25000 }),
      axios.get(`${API}/api/foh/tables`,          { headers: hdrs(), timeout: 25000 }),
      axios.get(`${API}/api/foh/shifts/current`,  { headers: hdrs(), timeout: 25000 }),
    ]);
    if (m.status === "fulfilled") setMe(m.value.data?.staff);
    if (t.status === "fulfilled") setTables(t.value.data || []);
    if (sh.status === "fulfilled") setShift(sh.value.data?.shift || null);
    // 401 on auth/me → blow away session
    if (m.status === "rejected" && m.reason?.response?.status === 401) {
      localStorage.removeItem(SK); setToken("");
    }
  }, []);

  useEffect(() => { if (token) refresh(); }, [token, refresh]);

  // If first refresh returns empty tables (likely R2-backfill timeout),
  // retry once after a short delay so the tablet self-heals.
  useEffect(() => {
    if (!token) return;
    if (tables.length > 0) return;
    const t = setTimeout(() => refresh(), 4500);
    return () => clearTimeout(t);
  }, [token, tables.length, refresh]);

  const loadCheck = async (table) => {
    setActive(table);
    if (!table?.current_check_id) { setCheck(null); return; }
    const r = await axios.get(`${API}/api/foh/checks/${table.current_check_id}`,
      { headers: hdrs() });
    setCheck(r.data);
  };

  if (!token) return <PinLogin onAuth={(t) => {
    localStorage.setItem(SK, t); setToken(t);
  }} />;

  return (
    <div style={s.root} data-testid="foh-root">
      <header style={s.topbar}>
        <div>
          <span style={s.brand}>HustleEats</span>{" "}
          <span style={s.subBrand}>FOH</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {shift ? (
            <span style={s.pillOk}>
              <Coffee size={12} /> Shift open · ${shift.opening_cash}
            </span>
          ) : (
            <ShiftOpener onOpened={refresh} />
          )}
          {shift && <ShiftCloser shift={shift} onClosed={refresh} />}
          <span style={s.pill}>
            {me?.name} · {me?.role}
          </span>
          <button onClick={async () => {
            try {
              await axios.post(`${API}/api/foh/auth/logout`, {}, { headers: hdrs() });
            } catch { /* ignore */ }
            localStorage.removeItem(SK); setToken("");
          }} style={s.logout} data-testid="foh-logout">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </header>

      <div style={s.body}>
        <main style={s.floor}>
          {!shift && (
            <div style={s.warn}>
              <AlertTriangle size={14} /> Open a shift before seating
              tables. Use the green button above.
            </div>
          )}
          <div style={s.tableGrid}>
            {tables.length === 0 && (
              <div style={s.empty}>
                No tables defined yet. Have an admin POST to
                <code>{` /api/foh/tables `}</code> to seed your floor plan.
              </div>
            )}
            {tables.map(t => (
              <button key={t.id}
                      data-testid={`foh-table-${t.label}`}
                      onClick={() => loadCheck(t)}
                      style={{ ...s.tile,
                                background: STATUS_BG[t.status],
                                color: STATUS_FG[t.status],
                                borderColor: active?.id === t.id ? "#0f172a" : "transparent" }}>
                <div style={s.tileLabel}>{t.label}</div>
                <div style={s.tileSeats}>{t.seats} seats</div>
                <div style={s.tileStatus}>{t.status}</div>
              </button>
            ))}
          </div>
        </main>

        <CheckDrawer
          table={active}
          check={check}
          shift={shift}
          loading={loading}
          onAction={async (kind, payload) => {
            setLoading(true);
            try {
              await runTableAction(active, check, kind, payload);
              await refresh();
              if (active) {
                const t2 = (await axios.get(`${API}/api/foh/tables`,
                  { headers: hdrs() })).data.find(x => x.id === active.id);
                if (t2) loadCheck(t2);
              }
            } catch (e) {
              alert(e?.response?.data?.detail || e.message);
            }
            setLoading(false);
          }}
          tables={tables}
        />
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Subcomponents
// ───────────────────────────────────────────────────────────────────

// Tiny stylesheet for the spinner (inline-styles can't do @keyframes)
const __fohSpinnerCSS = `
@keyframes foh-spin { to { transform: rotate(360deg); } }
.foh-spin { animation: foh-spin 0.8s linear infinite; display: inline-block;
            margin-right: 6px; vertical-align: middle; }
`;
if (typeof document !== "undefined" && !document.getElementById("foh-spin-css")) {
  const tag = document.createElement("style");
  tag.id = "foh-spin-css";
  tag.textContent = __fohSpinnerCSS;
  document.head.appendChild(tag);
}

function PinLogin({ onAuth }) {
  const [venue, setVenue] = useState(localStorage.getItem("foh_venue") || "");
  const [pin, setPin]     = useState("");
  const [err, setErr]     = useState("");
  const [busy, setBusy]   = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/api/foh/auth/pin`,
        { venue_slug: venue, pin }, { timeout: 30000 });
      localStorage.setItem("foh_venue", venue);
      onAuth(r.data.session_token);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Sign-in failed");
    }
    setBusy(false);
  };

  return (
    <div style={s.loginRoot} data-testid="foh-login">
      <form onSubmit={submit} style={s.loginCard}>
        <Lock size={22} color="#fbbf24" />
        <h1 style={s.loginH1}>FOH sign-in</h1>
        <input value={venue} onChange={e => setVenue(e.target.value)}
               required placeholder="Venue slug (e.g. your-venue)"
               style={s.loginInput} data-testid="foh-login-venue"
               disabled={busy} />
        <input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
               required placeholder="Your PIN" inputMode="numeric"
               maxLength={6} style={{ ...s.loginInput, fontSize: 24, textAlign: "center",
                                        letterSpacing: 8 }}
               data-testid="foh-login-pin"
               disabled={busy} />
        {err && <div style={s.loginErr} data-testid="foh-login-err">{err}</div>}
        <button type="submit" style={s.loginBtn}
                data-testid="foh-login-submit"
                disabled={busy}>
          {busy ? <Loader2 size={14} className="foh-spin" /> : <ArrowRight size={14} />}
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <small style={{ color: "#64748b", marginTop: 8 }}>
          Your manager seeds PINs via <code>POST /api/foh/staff</code>.
        </small>
      </form>
    </div>
  );
}

function ShiftOpener({ onOpened }) {
  const [cash, setCash] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!cash) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/foh/shifts/open`,
        { opening_cash: parseFloat(cash) }, { headers: hdrs() });
      onOpened();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <input value={cash} onChange={e => setCash(e.target.value)}
             type="number" min="0" step="0.01" placeholder="$ Opening cash"
             style={s.cashInput} data-testid="foh-shift-open-input" />
      <button onClick={submit} disabled={busy} style={s.pillCtaOk}
              data-testid="foh-shift-open-btn">
        {busy ? <Loader2 size={12} className="spin" /> : <Coffee size={12} />} Open shift
      </button>
    </div>
  );
}

function ShiftCloser({ shift, onClosed }) {
  const [open, setOpen] = useState(false);
  const [cash, setCash] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/foh/shifts/close`,
        { closing_cash: parseFloat(cash || 0) }, { headers: hdrs() });
      setDone(r.data);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} style={s.pillCta}
            data-testid="foh-shift-close-btn">
      <Wallet size={12} /> Close shift
    </button>
  );

  return (
    <div style={s.modalBg} onClick={() => { setOpen(false); if (done) onClosed(); }}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        {!done ? (
          <>
            <h3 style={{ margin: 0, fontFamily: "Georgia, serif" }}>Close shift</h3>
            <p style={{ color: "#64748b", fontSize: 13 }}>
              Count the cash in the drawer and enter the total below.
            </p>
            <input value={cash} onChange={e => setCash(e.target.value)}
                   type="number" placeholder="$ Closing cash" autoFocus
                   style={{ ...s.cashInput, width: "100%", fontSize: 22, padding: 14 }}
                   data-testid="foh-shift-close-input" />
            <button onClick={submit} disabled={busy} style={s.modalBtn}
                    data-testid="foh-shift-close-submit">
              {busy ? "Closing…" : "Close shift"} <ArrowRight size={14} />
            </button>
          </>
        ) : (
          <>
            <CheckCircle2 size={28} color="#16a34a" />
            <h3 style={{ margin: 0, fontFamily: "Georgia, serif" }}>Shift closed</h3>
            <ul style={{ listStyle: "none", padding: 0, fontSize: 13, lineHeight: 1.8 }}>
              <li>Opening cash:   <strong>${done.opening_cash.toFixed(2)}</strong></li>
              <li>Cash collected: <strong>${done.cash_collected.toFixed(2)}</strong></li>
              <li>Expected:       <strong>${done.expected_cash.toFixed(2)}</strong></li>
              <li>Counted:        <strong>${done.closing_cash.toFixed(2)}</strong></li>
              <li style={{ color: done.variance === 0 ? "#16a34a"
                                 : Math.abs(done.variance) < 5 ? "#92400e" : "#991b1b" }}>
                Variance: <strong>${done.variance.toFixed(2)}</strong>
              </li>
            </ul>
            <button onClick={() => { setOpen(false); onClosed(); }}
                    style={s.modalBtn} data-testid="foh-shift-close-done">
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CheckDrawer({ table, check, shift, onAction, tables }) {
  const [label, setLabel] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty]     = useState(1);

  if (!table) return (
    <aside style={s.drawer} data-testid="foh-drawer-empty">
      <p style={{ color: "#64748b" }}>Select a table on the left.</p>
    </aside>
  );

  const addItem = async () => {
    if (!label || !price) return;
    await onAction("add-item", { label, qty: parseInt(qty, 10) || 1,
                                  unit_price: parseFloat(price) });
    setLabel(""); setPrice(""); setQty(1);
  };

  return (
    <aside style={s.drawer} data-testid={`foh-drawer-${table.label}`}>
      <header style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontFamily: "Georgia, serif" }}>
          Table {table.label}
          <small style={{ display: "block", color: "#64748b", fontSize: 11,
                           marginTop: 4, fontFamily: "Inter, sans-serif",
                           textTransform: "uppercase", letterSpacing: 0.6 }}>
            {table.status} · {table.seats} seats
          </small>
        </h2>
        <select value={table.status} disabled={!shift}
                onChange={e => onAction("set-status", { status: e.target.value })}
                style={s.statusSelect} data-testid="foh-status-select">
          {Array.from(["open", "seated", "ordering", "billed", "cleaning"]).map(x => (
            <option key={x} value={x}>{x}</option>
          ))}
        </select>
      </header>

      {check ? (
        <>
          <div style={{ background: "white", border: "1px solid #e8e2d4",
                         borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#64748b",
                           textTransform: "uppercase", letterSpacing: 0.5,
                           marginBottom: 8 }}>
              Check items ({check.items?.length || 0})
            </div>
            {(check.items || []).map(it => (
              <div key={it.item_id}
                   style={{ display: "flex", justifyContent: "space-between",
                             padding: "6px 0",
                             borderBottom: "1px solid #f1f5f9", fontSize: 13 }}
                   data-testid={`foh-item-${it.item_id}`}>
                <div>
                  <strong>{it.qty}×</strong> {it.label}
                  {it.notes && <em style={{ color: "#94a3b8" }}> · {it.notes}</em>}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  ${it.line_total.toFixed(2)}
                  <button onClick={() => onAction("remove-item", { item_id: it.item_id })}
                          style={s.iconBtn} title="Remove">
                    <Minus size={12} />
                  </button>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #e8e2d4",
                            display: "flex", justifyContent: "space-between",
                            fontWeight: 700 }}>
              <span>Total</span>
              <span data-testid="foh-check-total">${check.total.toFixed(2)}</span>
            </div>
          </div>

          {/* Quick-add */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto",
                          gap: 6, marginBottom: 14 }}>
            <input value={label} onChange={e => setLabel(e.target.value)}
                   placeholder="Item label" style={s.itemInput}
                   data-testid="foh-item-label" />
            <input value={qty} onChange={e => setQty(e.target.value)}
                   type="number" min="1" placeholder="Qty" style={s.itemInput}
                   data-testid="foh-item-qty" />
            <input value={price} onChange={e => setPrice(e.target.value)}
                   type="number" min="0" step="0.01" placeholder="$ each"
                   style={s.itemInput} data-testid="foh-item-price" />
            <button onClick={addItem} disabled={!label || !price}
                    style={s.miniCta} data-testid="foh-add-item">
              <Plus size={14} />
            </button>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            <PayButton onPay={(p) => onAction("pay", p)} total={check.total}
                       remaining={Math.max(0, check.total - (check.paid_total || 0))} />
            <TransferButton current={table} tables={tables}
                             onTransfer={(to_table_id) =>
                               onAction("transfer", { to_table_id })} />
            <SplitButton items={check.items || []}
                          onSplit={(item_ids) => onAction("split", { item_ids })} />
          </div>
        </>
      ) : (
        <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
          {table.status === "open"
            ? "Tap the status dropdown above and choose 'seated' to start a check."
            : `Table is ${table.status}; no active check.`}
        </div>
      )}
    </aside>
  );
}

function PayButton({ onPay, remaining }) {
  const [open, setOpen] = useState(false);
  const [amt, setAmt]   = useState("");
  const [method, setMethod] = useState("cash");
  if (!open) return (
    <button onClick={() => { setOpen(true); setAmt(remaining.toFixed(2)); }}
            style={s.actionBtnPrimary} data-testid="foh-pay-btn"
            disabled={remaining <= 0}>
      <DollarSign size={14} /> Take payment
      {remaining > 0 && ` · $${remaining.toFixed(2)}`}
    </button>
  );
  return (
    <div style={s.popover}>
      <select value={method} onChange={e => setMethod(e.target.value)}
              style={s.itemInput} data-testid="foh-pay-method">
        <option value="cash">Cash</option>
        <option value="card-manual">Card (manual entry)</option>
        <option value="comp">Comp</option>
      </select>
      <input value={amt} onChange={e => setAmt(e.target.value)} type="number"
             min="0" step="0.01" style={s.itemInput} autoFocus
             data-testid="foh-pay-amount" />
      <button onClick={async () => {
        await onPay({ method, amount: parseFloat(amt) });
        setOpen(false);
      }} style={s.miniCta} data-testid="foh-pay-submit">
        <ArrowRight size={14} />
      </button>
      <button onClick={() => setOpen(false)} style={s.iconBtn}>
        <X size={14} />
      </button>
    </div>
  );
}

function TransferButton({ current, tables, onTransfer }) {
  const [open, setOpen] = useState(false);
  const candidates = tables.filter(t => t.id !== current.id &&
    (t.status === "open" || t.status === "cleaning"));
  if (!open) return (
    <button onClick={() => setOpen(true)} style={s.actionBtn}
            data-testid="foh-transfer-btn">
      <Move size={14} /> Transfer
    </button>
  );
  return (
    <div style={s.popover}>
      {candidates.length === 0 && <small>No free tables.</small>}
      {candidates.map(t => (
        <button key={t.id} onClick={() => { onTransfer(t.id); setOpen(false); }}
                style={s.miniBtn} data-testid={`foh-transfer-to-${t.label}`}>
          → {t.label}
        </button>
      ))}
      <button onClick={() => setOpen(false)} style={s.iconBtn}><X size={14} /></button>
    </div>
  );
}

function SplitButton({ items, onSplit }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const toggle = (id) => {
    const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); setPicked(n);
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} style={s.actionBtn}
            data-testid="foh-split-btn" disabled={items.length < 2}>
      <Split size={14} /> Split
    </button>
  );
  return (
    <div style={{ ...s.popover, flexWrap: "wrap" }}>
      {items.map(it => (
        <label key={it.item_id} style={{ fontSize: 12, display: "inline-flex",
                                          gap: 4, alignItems: "center",
                                          background: picked.has(it.item_id) ? "#fef3c7" : "white",
                                          padding: "4px 8px", borderRadius: 8,
                                          border: "1px solid #e8e2d4" }}>
          <input type="checkbox" checked={picked.has(it.item_id)}
                 onChange={() => toggle(it.item_id)}
                 data-testid={`foh-split-pick-${it.item_id}`} />
          {it.label}
        </label>
      ))}
      <button onClick={async () => {
        await onSplit([...picked]);
        setOpen(false); setPicked(new Set());
      }} disabled={picked.size === 0} style={s.miniCta}
              data-testid="foh-split-submit">
        <Combine size={14} /> Split
      </button>
      <button onClick={() => setOpen(false)} style={s.iconBtn}><X size={14} /></button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
async function runTableAction(table, check, kind, payload) {
  if (kind === "set-status") {
    return axios.post(`${API}/api/foh/tables/${table.id}/status`,
      payload, { headers: hdrs() });
  }
  if (kind === "add-item" && check) {
    return axios.post(`${API}/api/foh/checks/${check.id}/items`,
      payload, { headers: hdrs() });
  }
  if (kind === "remove-item" && check) {
    return axios.delete(`${API}/api/foh/checks/${check.id}/items/${payload.item_id}`,
      { headers: hdrs() });
  }
  if (kind === "pay" && check) {
    return axios.post(`${API}/api/foh/checks/${check.id}/pay`,
      payload, { headers: hdrs() });
  }
  if (kind === "transfer" && check) {
    return axios.post(`${API}/api/foh/checks/${check.id}/transfer`,
      payload, { headers: hdrs() });
  }
  if (kind === "split" && check) {
    return axios.post(`${API}/api/foh/checks/${check.id}/split`,
      payload, { headers: hdrs() });
  }
}

// ───────────────────────────────────────────────────────────────────
const STATUS_BG = {
  open:     "#f1f5f9", seated: "#fef3c7", ordering: "#fde68a",
  billed:   "#dcfce7", cleaning: "#e0e7ff",
};
const STATUS_FG = {
  open:     "#475569", seated: "#92400e", ordering: "#7c2d12",
  billed:   "#166534", cleaning: "#3730a3",
};
const s = {
  root: { background: "#fffef5", minHeight: "100vh",
          fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" },
  topbar: { display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "14px 18px",
            background: "#0f172a", color: "#fbbf24", flexWrap: "wrap", gap: 12 },
  brand: { fontFamily: "Georgia, serif", fontWeight: 800, fontSize: 22, letterSpacing: -0.5 },
  subBrand: { fontSize: 11, color: "#94a3b8", textTransform: "uppercase",
              letterSpacing: 0.6, marginLeft: 6 },
  pill: { background: "#1e293b", color: "#94a3b8",
          padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600 },
  pillOk: { background: "#16a34a", color: "white", display: "inline-flex",
            alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999,
            fontSize: 12, fontWeight: 700 },
  pillCta: { display: "inline-flex", alignItems: "center", gap: 6,
              background: "#fbbf24", color: "#0f172a", border: "none",
              padding: "7px 14px", borderRadius: 999, fontWeight: 700,
              fontSize: 12, cursor: "pointer" },
  pillCtaOk: { display: "inline-flex", alignItems: "center", gap: 6,
                background: "#16a34a", color: "white", border: "none",
                padding: "7px 14px", borderRadius: 999, fontWeight: 700,
                fontSize: 12, cursor: "pointer" },
  cashInput: { padding: "6px 12px", border: "1px solid #1e293b",
                background: "#0f172a", color: "#fbbf24", borderRadius: 999,
                fontSize: 12, width: 130, outline: "none" },
  logout: { background: "transparent", color: "#94a3b8",
             border: "1px solid #1e293b", padding: "6px 12px",
             borderRadius: 999, cursor: "pointer", fontSize: 11,
             display: "inline-flex", alignItems: "center", gap: 4 },
  body: { display: "grid", gridTemplateColumns: "1fr 380px",
          gap: 14, padding: 14, minHeight: "calc(100vh - 64px)" },
  floor: { background: "white", border: "1px solid #e8e2d4",
            borderRadius: 14, padding: 18 },
  warn: { background: "#fee2e2", color: "#991b1b", padding: 10,
           borderRadius: 8, fontSize: 13, marginBottom: 14,
           display: "inline-flex", alignItems: "center", gap: 6 },
  tableGrid: { display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: 12 },
  tile: { aspectRatio: 1, borderRadius: 14, border: "3px solid transparent",
           padding: 12, display: "flex", flexDirection: "column",
           justifyContent: "space-between", cursor: "pointer",
           fontFamily: "inherit", textAlign: "left" },
  tileLabel: { fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 800,
                letterSpacing: -1 },
  tileSeats: { fontSize: 11, opacity: 0.8 },
  tileStatus: { fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6,
                 fontWeight: 700 },
  drawer: { background: "#fffbeb", border: "1px solid #fde68a",
            borderRadius: 14, padding: 18 },
  statusSelect: { padding: "6px 10px", border: "1px solid #e8e2d4",
                   borderRadius: 8, fontSize: 12, fontFamily: "inherit" },
  itemInput: { padding: "8px 10px", border: "1px solid #e8e2d4",
                borderRadius: 8, fontSize: 13, fontFamily: "inherit",
                outline: "none", width: "100%", boxSizing: "border-box" },
  miniCta: { background: "#0f172a", color: "#fbbf24", border: "none",
              padding: "0 14px", borderRadius: 8, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
              fontWeight: 700, fontSize: 12 },
  miniBtn: { background: "#fbbf24", color: "#0f172a", border: "none",
              padding: "6px 12px", borderRadius: 8, cursor: "pointer",
              fontSize: 12, fontWeight: 700 },
  iconBtn: { background: "transparent", border: "none", color: "#94a3b8",
              cursor: "pointer", padding: 4 },
  actionBtn: { display: "inline-flex", alignItems: "center", gap: 4,
                background: "white", color: "#0f172a",
                border: "1px solid #e8e2d4", padding: "8px 14px",
                borderRadius: 999, fontWeight: 700, fontSize: 12,
                cursor: "pointer" },
  actionBtnPrimary: { display: "inline-flex", alignItems: "center", gap: 4,
                       background: "#16a34a", color: "white", border: "none",
                       padding: "8px 14px", borderRadius: 999, fontWeight: 700,
                       fontSize: 12, cursor: "pointer" },
  popover: { display: "flex", gap: 6, alignItems: "center",
              background: "white", border: "1px solid #e8e2d4",
              borderRadius: 10, padding: 6, marginBottom: 8, width: "100%" },
  empty: { gridColumn: "1 / -1", textAlign: "center",
            color: "#64748b", padding: 30 },
  loginRoot: { minHeight: "100vh", background: "#0f172a",
                display: "flex", alignItems: "center",
                justifyContent: "center", padding: 22 },
  loginCard: { background: "white", padding: 32, borderRadius: 18,
                maxWidth: 360, width: "100%",
                display: "flex", flexDirection: "column", gap: 12 },
  loginH1: { fontFamily: "Georgia, serif", margin: 0, fontSize: 26 },
  loginInput: { padding: "12px 14px", border: "1px solid #e8e2d4",
                 borderRadius: 10, fontSize: 16, fontFamily: "inherit",
                 outline: "none" },
  loginBtn: { background: "#0f172a", color: "#fbbf24", border: "none",
               padding: "12px 22px", borderRadius: 999, fontWeight: 800,
               cursor: "pointer", display: "inline-flex", justifyContent: "center",
               alignItems: "center", gap: 6, fontSize: 14 },
  loginErr: { background: "#fee2e2", color: "#991b1b", padding: 10,
               borderRadius: 8, fontSize: 13 },
  modalBg: { position: "fixed", inset: 0, background: "rgba(15,23,42,.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 50, padding: 20 },
  modal: { background: "white", borderRadius: 14, padding: 26,
            maxWidth: 420, width: "100%",
            display: "flex", flexDirection: "column", gap: 12 },
  modalBtn: { background: "#0f172a", color: "#fbbf24", border: "none",
               padding: "12px 22px", borderRadius: 999, fontWeight: 800,
               cursor: "pointer", display: "inline-flex", justifyContent: "center",
               alignItems: "center", gap: 6, fontSize: 14 },
};
